import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { vendorKeys } from '@config/queryKeys';
import { useOrganization } from '@context/OrganizationContext';
import { useSnackbar } from '@goodfynd/context';
import { InputCurrency } from '@goodfynd/forms';
import { strings } from '@goodfynd/strings';
import { Text } from '@goodfynd/typography';
import { useVendorPayouts } from '@hooks/usePayouts';
import { renderPrice } from '@utils/number-util';
import { calculateInstantPayoutFee } from '@utils/order-util';
import { stringUtil } from 'utils/index';
import { z } from 'zod';

import { useVendorDispatch } from '../../../context/VendorContext';
import { usePayoutsApi } from '../../../hooks/api';
import { ModalForm } from '../../forms';

import type { FormFields, InstantPayoutModalProps } from './types';

const instantPayoutDescription = `Get up to :maxAmount paid out instantly. A 1.25% fee will apply.`;

export default function VendorInstantPayoutModal({
  ...props
}: InstantPayoutModalProps<FormFields>) {
  const { organizationNameId } = useOrganization();
  const api = usePayoutsApi();
  const queryClient = useQueryClient();
  const { balances, instantPayoutsEnabled } = useVendorPayouts();
  const { launchStripeDashboard } = useVendorDispatch();
  const amountInstantlyAvailable = balances.instant || 0;
  const calculateFinalPayout = (payoutAmount: number) =>
    payoutAmount - calculateInstantPayoutFee(payoutAmount);

  const [busy, toggleBusy] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState(balances.instant);
  const { openSnackbar } = useSnackbar();

  const handleSubmit = async (data: FormFields) => {
    toggleBusy(true);

    if (instantPayoutsEnabled) {
      try {
        const {
          message = `Your ${renderPrice(
            calculateFinalPayout(amountInstantlyAvailable)
          )} is on the way!`,
        } = await api.requestInstantPayout(organizationNameId, data.amount);
        queryClient.invalidateQueries({
          queryKey: vendorKeys.payouts(organizationNameId),
        });

        openSnackbar({
          message,
          type: 'success',
        });
        props.close();
      } catch (error) {
        console.error(error);
        openSnackbar({
          message: 'Unable to request payout at this time.',
          type: 'warning',
        });
      }
    } else {
      await launchStripeDashboard();
      props.close();
    }

    toggleBusy(false);
  };

  return (
    <>
      <ModalForm<FormFields>
        {...props}
        busy={busy}
        onSubmit={handleSubmit}
        defaultValues={{
          amount: balances.instant,
        }}
        header={
          <Text as="h2" type="h5">
            {strings.titles.instantPayout}
          </Text>
        }
        renderFields={({ control, watch }) => {
          watch(({ amount }) => {
            setPayoutAmount(amount || 0);
          });
          const fee = calculateInstantPayoutFee(payoutAmount);
          return instantPayoutsEnabled ? (
            <>
              <Text>
                {stringUtil.replace(instantPayoutDescription, {
                  ':maxAmount': renderPrice(balances.instant),
                })}
              </Text>

              <InputCurrency<FormFields>
                control={control}
                defaultValue={balances.instant}
                helperText={fee > 0 ? `Fee: ${renderPrice(fee)}` : undefined}
                label="Payout amount"
                name="amount"
              />
            </>
          ) : (
            <Text>
              In order to be eligible for instant payouts, a debit card must be
              associated with your Stripe account.
            </Text>
          );
        }}
        submitButtonText={
          instantPayoutsEnabled
            ? `Deposit ${renderPrice(
                calculateFinalPayout(payoutAmount)
              )} instantly!`
            : 'Add Debit Card'
        }
        validationSchema={z.object({
          amount: z
            .number()
            .min(40, `Instant pay out amount must be at least $40`)
            .max(
              balances.instant,
              `You can instant pay out a max amount of ${renderPrice(
                balances.instant
              )}`
            )
            .nullable()
            .refine((value) => value !== null, {
              message: 'Amount is required.',
            }),
        })}
      />
    </>
  );
}
